import { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { startCapturaOrigenDestino, startGetAsistenciaInicial } from "../../../../context/actions/crearServiciosV2Actions";
import { setEsperandoSMS } from "../../../../context/actions/capturaActions";
import { useGeolocation } from "./useGeolocation";
import { useWhatsAppUbicacion } from "./useWhatsAppUbicacion";

const parseDatosRedux = (captura, sufijo) => {
  const lat = captura[`lat${sufijo}`];
  const lng = captura[`lng${sufijo}`];
  if (!lat || !lng) return null;
  return {
    direccionCompleta: captura[`direccion${sufijo}`] || "",
    estado: captura[`estado${sufijo}`] || "",
    municipio: captura[`municipio${sufijo}`] || "",
    cp: captura[`cp${sufijo}`] || "",
    lat: parseFloat(lat),
    lng: parseFloat(lng),
  };
};

const tieneUbicacionCompleta = (datos) => {
  if (datos?.lat === null || datos?.lat === undefined || datos?.lat === "") return false;
  if (datos?.lng === null || datos?.lng === undefined || datos?.lng === "") return false;

  return [
    datos.direccionCompleta,
    datos.estado,
    datos.municipio,
    datos.cp,
  ].every((valor) => valor?.toString().trim());
};

export const useOrigenDestino = (asistenciaID) => {
  const dispatch = useDispatch();
  const { captura } = useSelector((state) => state.asistenciasReducer);
  const requiereDestino = captura.requiereDestino === true;

  const { getAddressByCoords, loading: loadingGeocode } = useGeolocation();
  const { enviarWhatsApp, esperandoSMS, coordenadas } = useWhatsAppUbicacion();

  const [datosOrigen, setDatosOrigen] = useState(null);
  const [datosDestino, setDatosDestino] = useState(null);
  const [ubicacionRecibida, setUbicacionRecibida] = useState(null);
  const [referenciaVisualOrigen, setReferenciaVisualOrigen] = useState("");
  const [loadingGuardar, setLoadingGuardar] = useState(false);

  const origenRedux = useMemo(() => parseDatosRedux(captura, "Origen"), [
    captura.latOrigen, captura.lngOrigen, captura.direccionOrigen,
    captura.estadoOrigen, captura.municipioOrigen, captura.cpOrigen,
  ]);

  const destinoRedux = useMemo(() => parseDatosRedux(captura, "Destino"), [
    captura.latDestino, captura.lngDestino, captura.direccionDestino,
    captura.estadoDestino, captura.municipioDestino, captura.cpDestino,
  ]);

  useEffect(() => {
    if (origenRedux && !datosOrigen) setDatosOrigen(origenRedux);
  }, [origenRedux, datosOrigen]);

  useEffect(() => {
    if (destinoRedux && !datosDestino) setDatosDestino(destinoRedux);
  }, [destinoRedux, datosDestino]);

  useEffect(() => {
    if (!coordenadas?.lat || !coordenadas?.lng || esperandoSMS !== 2) return;
    getAddressByCoords(coordenadas.lat, coordenadas.lng).then((direccion) => {
      if (direccion) {
        setUbicacionRecibida(direccion);
        dispatch(setEsperandoSMS(0));
      }
    });
  }, [coordenadas, esperandoSMS, getAddressByCoords, dispatch]);

  const actualizarOrigen = useCallback((datos) => {
    setDatosOrigen(datos);
    setUbicacionRecibida(null);
  }, []);
  const actualizarDestino = useCallback((datos) => setDatosDestino(datos), []);

  const puedeGuardar = tieneUbicacionCompleta(datosOrigen) && (!requiereDestino || tieneUbicacionCompleta(datosDestino));

  const guardarUbicaciones = useCallback(async () => {
    if (!puedeGuardar) return { success: false };

    const body = {
      asistenciaID,
      direccionOrigen: datosOrigen.direccionCompleta || "",
      estadoOrigen: datosOrigen.estado || "",
      latOrigen: datosOrigen.lat.toString(),
      lngOrigen: datosOrigen.lng.toString(),
      municipioOrigen: datosOrigen.municipio || "",
      cpOrigen: datosOrigen.cp || "",
      referenciaVisualOrigen: referenciaVisualOrigen || "",
      direccionDestino: datosDestino?.direccionCompleta || "",
      estadoDestino: datosDestino?.estado || "",
      latDestino: datosDestino?.lat?.toString() || "",
      lngDestino: datosDestino?.lng?.toString() || "",
      municipioDestino: datosDestino?.municipio || "",
      cpDestino: datosDestino?.cp || "",
    };

    setLoadingGuardar(true);
    const result = await dispatch(startCapturaOrigenDestino(body));
    if (result?.success) await dispatch(startGetAsistenciaInicial(asistenciaID));
    setLoadingGuardar(false);
    return result;
  }, [asistenciaID, datosOrigen, datosDestino, referenciaVisualOrigen, puedeGuardar, dispatch]);

  return {
    datosOrigen,
    datosDestino,
    ubicacionRecibida,
    requiereDestino,
    referenciaVisualOrigen,
    setReferenciaVisualOrigen,
    loadingGuardar,
    loadingGeocode,
    puedeGuardar,
    esperandoSMS,
    enviarWhatsApp,
    actualizarOrigen,
    actualizarDestino,
    guardarUbicaciones,
  };
};
